import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import api from './api-client'
import uuid from './pseudo-uuid'
import { DevicePermissions, PermissionRecord, PermissionType, User } from '@ecountabl/lib'
import { Storage } from './utility'
import { Platform } from 'react-native'

const createAndStoreDeviceId = async (): Promise<string> => {
  const id = `${uuid()}-${Platform.OS}`
  await Storage.setItem('device-id', id)
  return id
}

export const loadOrCreateDeviceId = createAsyncThunk('auth/loadOrCreateDeviceId', async () => {
  const id = await Storage.getItem('device-id')

  if (!id) {
    const newId = await createAndStoreDeviceId()
    return newId
  }

  return id
})

export const getMe = createAsyncThunk('auth/getMe', async (_, _api) => {
  try {
    const user = await api.getMe()

    return user
  } catch (err) {
    return _api.rejectWithValue(err.message || err.msg)
  }
})

export const syncCustomerData = createAsyncThunk('auth/syncCustomerData', async (_, _api) => {
  try {
    const user = await api.getMe()

    return user
  } catch (err) {
    return _api.rejectWithValue(err.message || err.msg)
  }
})

export const completeOnboarding = createAsyncThunk('auth/completeOnboarding', async (_, _api) => {
  try {
    await api.onboard()
    return true
  } catch (err) {
    return _api.rejectWithValue(err.message || err.msg)
  }
})

interface AuthState {
  status: 'idle' | 'loading' | 'success' | 'error';
  syncStatus: 'idle' | 'loading' | 'success' | 'error';
  user: Partial<User>;
  error: string;
  deviceId: string;
}

const initialState: AuthState = {
  status: 'idle',
  syncStatus: 'idle',
  user: {},
  error: '',
  deviceId: '',
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
    },
    setUserField(state: AuthState, action: PayloadAction<{ field: string, value: any }>): void {
      const user = { ...state.user }
      user[action.payload.field] = action.payload.value
      state.user = user
    },
    clearAuthErrors: (state) => {
      state.error = ''
    },
    setAppPermissions: (state: AuthState, action: PayloadAction<DevicePermissions>) => {
      if (!state.user.appPermissions) {
        state.user.appPermissions = {
          [state.deviceId]: action.payload
        }
        return
      }

      state.user.appPermissions[state.deviceId] = action.payload
    },
    setAppPermission: (
      state: AuthState, action: PayloadAction<{type: PermissionType, permission: PermissionRecord}>
    ) => {
      if (!state.user.appPermissions) {
        state.user.appPermissions = {
          [state.deviceId]: {
            'location': action.payload.type === 'location' ? action.payload.permission : undefined,
            'notifications': action.payload.type === 'notifications' ? action.payload.permission : undefined
          }
        }

        return
      }

      const currentPermission = state.user.appPermissions[state.deviceId]
      if (!currentPermission) {
        state.user.appPermissions[state.deviceId] = {
          'location': action.payload.type === 'location' ? action.payload.permission : undefined,
          'notifications': action.payload.type === 'notifications' ? action.payload.permission : undefined
        }
      }

      if (action.payload.type === 'location') {
        currentPermission.location = action.payload.permission
      } else if (action.payload.type === 'notifications') {
        currentPermission.notifications = action.payload.permission
      }
      
      state.user.appPermissions[state.deviceId] = currentPermission
    },
    logOut: (state: AuthState): void => {
      state.user = {}
      state.error = ''
      state.status = 'idle'
      state.syncStatus = 'idle'
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadOrCreateDeviceId.fulfilled, (state, action) => {
        state.deviceId = action.payload
      })
      .addCase(getMe.pending, (state) => {
        state.status = 'loading'
        state.error = ''
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.user = action.payload
        state.status = 'success'
      })
      .addCase(getMe.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to fetch user data'
        state.status = 'error'
      })
      .addCase(syncCustomerData.pending, (state) => {
        state.syncStatus = 'loading'
      })
      .addCase(syncCustomerData.fulfilled, (state, action) => {
        state.user = action.payload
        state.syncStatus = 'success'
      })
      .addCase(syncCustomerData.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to sync customer data'
        state.syncStatus = 'error'
      })
      .addCase(completeOnboarding.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(completeOnboarding.fulfilled, (state) => {
        state.user.hasOnboarded = true
        state.status = 'success'
      })
      .addCase(completeOnboarding.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to complete onboarding'
        state.status = 'error'
      })
  },
})

export default authSlice.reducer
export const {
  clearAuthErrors,
  setUser,
  setUserField,
  setAppPermissions,
  setAppPermission,
  logOut,
} = authSlice.actions
